import {
  FullscreenDialog,
  HSpacer,
  IconButton,
  InfoTooltip,
  Text,
  VSpacer,
} from '@/components/DesignSystem';
import { Table, TableRow } from '@/components/DesignSystem/Table/Table';
import { QueryKeys } from '@/constants/QueryKeys';
import { buildProgramProgressText } from '@/pages/CustomerDashboard/CustomerDashboard';
import { formatHurdleText, getRankEarned, getNextTier, getTier } from '@/pages/FarmerList/helper';
import { RetailerApi } from '@/utilities/api/RetailerApi';
import { RewardsProgramApi } from '@/utilities/api/RewardsProgramApi';
import { ApiUser } from "@api/interfaces";
import { ApiFarmerRewards } from '@api/interfaces/ApiFarmerRewards';
import { ApiRewardsProgram } from '@api/interfaces/ApiRewardsProgram';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircle from '@mui/icons-material/CheckCircle';
import { Stack, useTheme } from '@mui/material';
import { getAccountName } from "@shared/utilities";
import i18next from 'i18next';
import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { AboutAccountRolesBanner } from "@/pages/CustomerDashboard/LinkedAccounts";
import { CIBPageName, UserType } from "@shared/enums";
import { UserApi } from "@/utilities/api/UserApi";
import { useTranslation } from 'react-i18next';

interface RewardsProgramProgressModalProps {
  customer: ApiUser,
  farmerRewards: ApiFarmerRewards | null,
  farmerRewardsProfiles?: ApiFarmerRewards[],
  onClose: () => void,
  open: boolean,
  rewardsProgram: ApiRewardsProgram,
}

export const RewardsProgramProgressModal = ({
  customer,
  farmerRewards,
  farmerRewardsProfiles,
  onClose,
  open,
  rewardsProgram,
}: RewardsProgramProgressModalProps) => {

  const { t } = useTranslation();
  const { retailerId } = rewardsProgram;
  const theme = useTheme();
  const [
    previousYearToCurrentYearCategoryIdMapping,
    setPreviousYearToCurrentYearCategoryIdMapping,
  ] = useState<{ [categoryId: string]: string }>({});

  const { data: categories } = useQuery(
    [QueryKeys.GET_RETAILER_CATEGORIES, retailerId],
    async () => RetailerApi.listCategories(retailerId),
  );

  const { data: linkedAccounts } = useQuery(
    [QueryKeys.GET_LINKED_ACCOUNTS, customer.id],
    () => UserApi.getLinkedAccounts(customer.id), {
      enabled: !!customer && customer.userType == UserType.Farmer,
    },
  );

  const { data: programs } = useQuery(
    [QueryKeys.LIST_REWARDS_PROGRAMS, customer.retailerId],
    () => RewardsProgramApi.list({
      retailerId: customer.retailerId!,
    }),
  );

  const previousYearProgram = programs?.find((program) => {
    const currentYearStartDate = new Date(rewardsProgram.startDate);
    currentYearStartDate.setDate(currentYearStartDate.getDate() - 1);
    const dayBefore = currentYearStartDate.toISOString().slice(0, 10);
    return program.endDate === dayBefore;
  }) ?? null;
  const previousYearFarmerRewards = farmerRewardsProfiles?.find(
    (rewards) => rewards.programId === previousYearProgram?.id,
  ) ?? null;

  const getCategoryName = useCallback(
    (categoryId: string, isParentCategory?: boolean) =>
      categories?.find(({ id, parentId }) => (
        id === categoryId &&
        (isParentCategory === undefined ||
          (isParentCategory ? parentId === null : parentId !== null))
      ))?.name,
    [categories],
  );

  useEffect(() => {
    if (!previousYearProgram?.tiers || !rewardsProgram.tiers) {
      return;
    }

    const mapping: { [categoryId: string]: string } = {};

    for (const currentYearTier of rewardsProgram.tiers) {
      for (const currentYearSegment of currentYearTier.segments ?? []) {
        const currentYearCategoryName = getCategoryName(currentYearSegment.categoryId, true);
        if (!currentYearCategoryName) {
          continue;
        }

        const prevTier = previousYearProgram.tiers.find((tier) =>
          tier.segments?.some((segment) => (
            getCategoryName(segment.categoryId, true) === currentYearCategoryName),
          ),
        );
        if (!prevTier) {
          continue;
        }

        for (const prevSegment of prevTier.segments ?? []) {
          const prevYearCategoryName = getCategoryName(prevSegment.categoryId, true);
          if (prevYearCategoryName !== currentYearCategoryName) {
            continue;
          }

          mapping[currentYearSegment.categoryId] = prevSegment.categoryId;

          if (currentYearSegment.subcategoryRewards && prevSegment.subcategoryRewards) {
            for (const currSubId of Object.keys(currentYearSegment.subcategoryRewards)) {
              const currSubName = getCategoryName(currSubId, false);
              if (!currSubName) {
                continue;
              }

              const prevSubId = Object.keys(prevSegment.subcategoryRewards).find((subId) =>
                getCategoryName(subId, false) === currSubName,
              );
              if (prevSubId) {
                mapping[currSubId] = prevSubId;
              }
            }
          }
        }
      }
    }

    setPreviousYearToCurrentYearCategoryIdMapping(mapping);
  }, [categories, getCategoryName, rewardsProgram, previousYearProgram]);

  const rankEarned = getRankEarned(farmerRewards, rewardsProgram);
  const tiers = rewardsProgram.tiers ?? [];
  const nextTier = getNextTier(rankEarned, tiers);

  const getTierToDisplay = () => {
    if (nextTier) {
      return nextTier;
    }
    if (rankEarned === null) {
      return getTier(0, tiers);
    }
    return getTier(rankEarned, tiers);
  };

  const tier = getTierToDisplay();
  const hasSomeSubcategories = !!tier?.segments?.some(
    (segment) => !!segment.subcategoryRewards,
  );
  const headers: (string | ReactNode)[] = [
    t($ => $.common.units.category),
    t($ => $.customers.progress.minimumHurdle),
    t($ => $.customers.progress.currentTotal),
    t($ => $.customers.progress.amountRemaining),
    '',
  ];
  if (previousYearProgram) {
    headers.push((
      <Stack direction="row">
        <Text category="label-large">{t($ => $.customers.progress.previousCYTotal)}</Text>
        <HSpacer size="2" />
        <InfoTooltip color="info">
          {t($ => $.customers.progress.previousCYTooltip)}
        </InfoTooltip>
      </Stack>
    ));
  }
  const TierTable = (
    !!categories && (
      <Table
        headers={headers}
      >
        {tier?.segments?.map(({
          categoryId,
          id,
          minimumHurdle,
          subcategoryRewards,
          uom,
        }) => {
          const hurdleText = formatHurdleText(minimumHurdle, uom, i18next);
          const rewardSegment = farmerRewards?.totalPerSegment[categoryId];
          const currentTotal = rewardSegment?.total ?? 0;
          const previousCategoryId = previousYearToCurrentYearCategoryIdMapping[categoryId];
          const previousRewardSegment = (
            previousYearFarmerRewards?.totalPerSegment[previousCategoryId]
          );
          const previousTotal = previousCategoryId ? previousRewardSegment?.total ?? 0 : null;
          let amountRemaining = 0;
          if (currentTotal < minimumHurdle) {
            amountRemaining = minimumHurdle - currentTotal;
          }
          const isComplete = amountRemaining === 0;
          const values = [
            ...(subcategoryRewards ? [] : hasSomeSubcategories ? [''] : ''),
            getCategoryName(categoryId),
            hurdleText,
            formatHurdleText(currentTotal, uom, i18next),
            formatHurdleText(amountRemaining, uom, i18next),
            isComplete && (
              <Stack alignItems="center" direction="row">
                <CheckCircle color="success" />
                <HSpacer size="2" />
                <Text category="label-small" color="success">
                  {t($ => $.customers.progress.complete)}
                </Text>
              </Stack>
            ),
          ];
          if (previousYearProgram) {
            values.push(
              previousTotal === null
                ? t($ => $.common.na)
                : formatHurdleText(previousTotal, uom, i18next),
            );
          }
          return (
            <TableRow
              expandedComponent={!subcategoryRewards ? undefined : (
                Object.entries(subcategoryRewards).map(([subcategoryId]) => {
                  const subcategoryTotal = (
                    rewardSegment?.subcategories?.[subcategoryId].total ?? 0
                  );
                  const previousSubcategoryId = (
                    previousYearToCurrentYearCategoryIdMapping[subcategoryId]
                  );
                  const previousSubcategoryTotal = previousSubcategoryId
                    ? previousRewardSegment?.subcategories?.[previousSubcategoryId]?.total ?? 0
                    : null;
                  let subcategoryAmountRemaining = 0;
                  if (currentTotal < minimumHurdle) {
                    subcategoryAmountRemaining = minimumHurdle - subcategoryTotal;
                  }
                  const values = [
                    '',
                    getCategoryName(subcategoryId),
                    '',
                    formatHurdleText(subcategoryTotal, uom, i18next),
                    formatHurdleText(subcategoryAmountRemaining, uom, i18next),
                    '',
                  ];
                  if (previousYearProgram) {
                    values.push(
                      previousSubcategoryTotal === null
                        ? t($ => $.common.na)
                        : formatHurdleText(previousSubcategoryTotal, uom, i18next),
                    );
                  }
                  return (
                    <TableRow
                      key={subcategoryId}
                      values={values}
                    />
                  );
                })
              )}
              key={id}
              values={values}
            />
          );
        })}
      </Table>
    )
  );

  return (
    <FullscreenDialog
      hideCloseButton
      maxWidth="lg"
      open={open}
      testID="rewards-program-progress-dialog"
      title={
        <Stack>
          <Text category="headline-small">
            {t($ => $.customers.progress.currentProgress)}
          </Text>
          <VSpacer size="1" />
          <Text category="title-small">
            {buildProgramProgressText(farmerRewards, rewardsProgram, t)}
          </Text>
        </Stack>
      }
      titleComponent={
        <IconButton
          onClick={onClose}
          sx={{ color: theme.palette.text.primary }}
          testID="back-to-customers-button"
        >
          <ArrowBackIcon />
        </IconButton>
      }
    >
      {!!customer.accountGroupRole && !!linkedAccounts?.length && (
        <>
          <AboutAccountRolesBanner
            customerRole={customer.accountGroupRole}
            linkedAccounts={linkedAccounts}
            pageName={CIBPageName.RP_CurrentProgress}
          />
          <VSpacer size="5" />
        </>
      )}
      <Stack direction="row">
        <Text category="body-medium" fontWeight={500}>
          {t($ => $.customers.progress.account)}
        </Text>
        <Text category="body-medium">
          &nbsp;{getAccountName(customer)}
        </Text>
      </Stack>
      <VSpacer size="7" />
      {TierTable}
    </FullscreenDialog>
  );
};
